import { CAN, PRO_CAN, type Capability, type SubtitleFormat } from '../../core';

/**
 * Which inline marks the Advanced edit dialog may offer for a given source
 * format (cue-forge-b0w), derived from the same core CAN/PRO_CAN capability data
 * behind the loss matrix — so the dialog never offers a mark the format cannot
 * express (the site-wide anti-overclaim principle). Phase 1 marks only.
 */

export type InlineMark = 'italic' | 'bold' | 'underline';

const PHASE1_MARKS: InlineMark[] = ['italic', 'bold', 'underline'];

/** Capability set for any format (core or Pro); SSA shares ASS's capabilities. */
function capsFor(format: SubtitleFormat): Set<Capability> {
  const key = format === 'ssa' ? 'ass' : format;
  if (key in CAN) return CAN[key as keyof typeof CAN];
  if (key in PRO_CAN) return PRO_CAN[key as keyof typeof PRO_CAN];
  return new Set();
}

/** The italic/bold/underline marks the format can carry, in a stable order. */
export function marksForFormat(format: SubtitleFormat): InlineMark[] {
  const caps = capsFor(format);
  return PHASE1_MARKS.filter((m) => caps.has(m));
}

export type ColorSupport = 'none' | 'freeform' | 'palette';

/**
 * How the colour control behaves: SCC exposes only the fixed 7 CEA-608 colours;
 * other colour-carrying formats take an arbitrary colour; the rest hide it.
 */
export function colorSupport(format: SubtitleFormat): ColorSupport {
  if (!capsFor(format).has('color')) return 'none';
  return format === 'scc' ? 'palette' : 'freeform';
}

/**
 * The 7 CEA-608 mid-row colours. `value` is the 608 colour *name* the SCC
 * serializer maps to a mid-row code (see formats/scc/scc.ts MIDROW_COLOR_INDEX) —
 * NOT a hex string; `swatch` is only the display colour for the picker.
 */
export const SCC_COLORS: readonly { label: string; value: string; swatch: string }[] = [
  { label: 'White', value: 'white', swatch: '#ffffff' },
  { label: 'Green', value: 'green', swatch: '#00ff00' },
  { label: 'Blue', value: 'blue', swatch: '#0000ff' },
  { label: 'Cyan', value: 'cyan', swatch: '#00ffff' },
  { label: 'Red', value: 'red', swatch: '#ff0000' },
  { label: 'Yellow', value: 'yellow', swatch: '#ffff00' },
  { label: 'Magenta', value: 'magenta', swatch: '#ff00ff' },
];

/** Whether Advanced editing offers anything for this format (else disable it). */
export function advancedAvailable(format: SubtitleFormat): boolean {
  return marksForFormat(format).length > 0 || colorSupport(format) !== 'none';
}
